import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api, { formatError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { UserPlus, Shield, User as UserIcon, KeyRound, Trash2, Loader2, X, Check, Mail, Lock, ChevronDown, Crown } from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [pwdFor, setPwdFor] = useState(null); // user obj for password change modal

  const fetchUsers = async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const setRole = async (u, role) => {
    if (u.role === role) return;
    try {
      await api.put(`/admin/users/${u.id}`, { role });
      setUsers((xs) => xs.map((x) => (x.id === u.id ? { ...x, role } : x)));
    } catch (e) {
      setErr(formatError(e));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(
      `Supprimer définitivement ${u.name} (${u.email}) ?\n\n` +
      `${u.rattrapages_count || 0} rattrapage(s) lui appartenant seront aussi supprimés. Cette action est irréversible.`
    )) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((xs) => xs.filter((x) => x.id !== u.id));
    } catch (e) {
      setErr(formatError(e));
    }
  };

  return (
    <AppShell>
      <div className="px-8 lg:px-12 py-10 max-w-[1400px]" data-testid="admin-users-page">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="label-xs mb-2">Administration · Utilisateurs</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Comptes du staff
            </h1>
            <p className="text-slate-600 mt-2 text-sm max-w-xl">
              Créez, modifiez et gérez les accès du staff pédagogique. Promouvez un membre administrateur pour
              partager les responsabilités de gestion.
            </p>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="open-create-user-btn"
            className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-slate-800"
          >
            <UserPlus size={16} /> Nouvel utilisateur
          </button>
        </div>

        {err && (
          <div className="mt-6 text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm" data-testid="admin-users-error">
            {err}
          </div>
        )}

        <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="label-xs">Utilisateurs</div>
            <div className="text-xs text-slate-500">{users.length} compte(s)</div>
          </div>
          {loading ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">Chargement…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  <th className="text-left px-5 py-3 font-semibold">Nom</th>
                  <th className="text-left px-5 py-3 font-semibold">Email</th>
                  <th className="text-left px-5 py-3 font-semibold">Rôle</th>
                  <th className="text-left px-5 py-3 font-semibold">Rattrapages</th>
                  <th className="text-left px-5 py-3 font-semibold">Créé le</th>
                  <th className="text-right px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === me?.id;
                  return (
                    <tr key={u.id} className="border-t border-slate-200 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                      <td className="px-5 py-3 font-semibold text-slate-900">
                        {u.name} {isSelf && <span className="text-xs font-normal text-slate-500">(vous)</span>}
                      </td>
                      <td className="px-5 py-3 text-slate-700">{u.email}</td>
                      <td className="px-5 py-3">
                        <RoleSelect user={u} disabled={isSelf} onChange={(r) => setRole(u, r)} canManageSuper={me?.role === "superadmin"} />
                      </td>
                      <td className="px-5 py-3 text-slate-700">{u.rattrapages_count || 0}</td>
                      <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(u.created_at)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setPwdFor(u)}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-slate-200 rounded-sm hover:bg-slate-100"
                            data-testid={`reset-pwd-${u.id}`}
                            title="Réinitialiser le mot de passe"
                          >
                            <KeyRound size={12} /> Mot de passe
                          </button>
                          <button
                            onClick={() => removeUser(u)}
                            disabled={isSelf}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-red-200 text-[#b91c1c] rounded-sm hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`delete-user-${u.id}`}
                            title={isSelf ? "Vous ne pouvez pas vous supprimer" : "Supprimer cet utilisateur"}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {createOpen && (
        <CreateUserDialog
          canCreateSuper={me?.role === "superadmin"}
          onClose={() => setCreateOpen(false)}
          onCreated={(u) => { setUsers((xs) => [u, ...xs]); setCreateOpen(false); }}
        />
      )}
      {pwdFor && (
        <PasswordDialog user={pwdFor} onClose={() => setPwdFor(null)} />
      )}
    </AppShell>
  );
}

function RoleSelect({ user, disabled, onChange, canManageSuper }) {
  const isAdmin = user.role === "admin";
  const isSuper = user.role === "superadmin";
  const Icon = isSuper ? Crown : isAdmin ? Shield : UserIcon;
  const tint = isSuper ? "bg-amber-50 text-amber-700 border-amber-200"
    : isAdmin ? "bg-blue-50 text-[#2563eb] border-blue-200"
    : "bg-slate-50 text-slate-700 border-slate-200";
  const iconColor = isSuper ? "text-amber-600" : isAdmin ? "text-[#2563eb]" : "text-slate-500";
  // If user is superadmin and current viewer isn't super, lock the row
  const lock = disabled || (isSuper && !canManageSuper);
  return (
    <div className="relative inline-flex">
      <select
        value={user.role}
        disabled={lock}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`role-select-${user.id}`}
        className={`appearance-none pl-7 pr-7 py-1 text-xs font-semibold rounded-sm border focus:outline-none focus:ring-1 focus:ring-slate-900 ${tint} disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        <option value="staff">Utilisateur</option>
        <option value="admin">Admin</option>
        {canManageSuper && <option value="superadmin">Super-admin</option>}
        {!canManageSuper && isSuper && <option value="superadmin">Super-admin</option>}
      </select>
      <Icon size={11} className={`absolute left-2 top-1/2 -translate-y-1/2 ${iconColor} pointer-events-none`} />
      <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  );
}

function CreateUserDialog({ onClose, onCreated, canCreateSuper }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("staff");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const roleOptions = canCreateSuper
    ? [
        { value: "staff", label: "Utilisateur", desc: "Création de rattrapages", Icon: UserIcon, color: "text-slate-500" },
        { value: "admin", label: "Administrateur", desc: "Gère utilisateurs + voit tous les rattrapages", Icon: Shield, color: "text-[#2563eb]" },
        { value: "superadmin", label: "Super-administrateur", desc: "Édition LLM + règles QCM", Icon: Crown, color: "text-amber-600" },
      ]
    : [
        { value: "staff", label: "Utilisateur", desc: "Création de rattrapages", Icon: UserIcon, color: "text-slate-500" },
        { value: "admin", label: "Administrateur", desc: "Gère utilisateurs + voit tous les rattrapages", Icon: Shield, color: "text-[#2563eb]" },
      ];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/users", { name, email, password, role });
      onCreated(data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="create-user-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus size={16} className="text-[#2563eb]" />
            <div className="font-display font-semibold text-slate-900">Nouvel utilisateur</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Nom complet" testid="new-user-name">
            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name" className="input" placeholder="Marie Dupont" />
          </Field>
          <Field label="Email">
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-user-email" className="input" placeholder="prenom.nom@ecole.fr" />
          </Field>
          <Field label="Mot de passe initial">
            <input required type="text" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-user-password" className="input font-mono" placeholder="Min. 6 caractères" />
          </Field>
          <Field label="Rôle">
            <div className={`grid grid-cols-1 gap-2 ${roleOptions.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
              {roleOptions.map(({ value, label, desc, Icon, color }) => (
                <button
                  type="button"
                  key={value}
                  onClick={() => setRole(value)}
                  data-testid={`new-user-role-${value}`}
                  className={`text-left px-3 py-2 border rounded-sm text-sm ${
                    role === value ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900 font-semibold" : "border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={13} className={color} />
                    <span>{label}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{desc}</div>
                </button>
              ))}
            </div>
          </Field>
          {err && <div className="text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm" data-testid="new-user-error">{err}</div>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-sm">Annuler</button>
          <button type="submit" disabled={busy} data-testid="submit-create-user" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0f172a] text-white text-sm font-semibold rounded-sm hover:bg-slate-800 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Créer
          </button>
        </div>
        <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; font-size: 0.875rem; border: 1px solid #cbd5e1; border-radius: 2px; }
          .input:focus { outline: none; border-color: #0f172a; box-shadow: 0 0 0 1px #0f172a; }`}</style>
      </form>
    </div>
  );
}

function PasswordDialog({ user, onClose }) {
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.put(`/admin/users/${user.id}`, { password: pwd });
      setDone(true);
      setTimeout(onClose, 1500);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="password-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock size={16} className="text-[#2563eb]" />
            <div className="font-display font-semibold text-slate-900">Réinitialiser le mot de passe</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm text-slate-700 flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2 rounded-sm">
            <Mail size={14} className="text-slate-500" />
            <span><strong>{user.name}</strong> · {user.email}</span>
          </div>
          <Field label="Nouveau mot de passe">
            <input
              required minLength={6} type="text" autoFocus
              value={pwd} onChange={(e) => setPwd(e.target.value)}
              data-testid="reset-pwd-input"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 font-mono"
              placeholder="Au moins 6 caractères"
            />
          </Field>
          <p className="text-xs text-slate-500">
            Communiquez ce mot de passe à l'utilisateur de manière sécurisée — il pourra le modifier ensuite.
          </p>
          {err && <div className="text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm">{err}</div>}
          {done && <div className="text-sm text-[#15803d] bg-green-50 border border-green-200 px-3 py-2 rounded-sm">Mot de passe mis à jour.</div>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-sm">Annuler</button>
          <button type="submit" disabled={busy || done} data-testid="submit-reset-pwd" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#2563eb] text-white text-sm font-semibold rounded-sm hover:bg-blue-700 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Mettre à jour
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-xs block mb-2">{label}</label>
      {children}
    </div>
  );
}
